import { api } from './client';

export interface TemplateDTO {
  id: string;
  accountId: string;
  type: 'reel' | 'post';
  backgroundColor: string;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  textColor: string;
  fontFamily: string;
  fontName?: string;
  fontUrl?: string;
  logoUrl?: string;
  autoFontMax?: number;
  colonBreak?: boolean;
  textZoneWidth?: number;
  bandGrow?: boolean;
  createdAt: string;
}

export interface CreateTemplateDTO {
  accountId: string;
  type: 'reel' | 'post';
  backgroundColor: string;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  textColor: string;
  fontFamily: string;
  fontName?: string;
  fontUrl?: string;
  logoUrl?: string;
  textBoxStyle?: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
  textBoxAngle?: number;
  textBoxRadius?: number;
  textBoxPadding?: number;
  textBoxPaddingTop?: number;
  textBoxPaddingLeft?: number;
  textBoxPaddingRight?: number;
  applyFontEffects?: boolean;
  reelBackgroundUrl?: string;
  reelTextCenterX?: number;
  reelTextBottom?: number;
  autoFontMax?: number;
  colonBreak?: boolean;
  textZoneWidth?: number;
  bandGrow?: boolean;
}

export interface UpdateTemplateDTO {
  type?: 'reel' | 'post';
  backgroundColor?: string;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  textColor?: string;
  fontFamily?: string;
  fontName?: string;
  fontUrl?: string;
  logoUrl?: string;
  textBoxStyle?: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
  textBoxAngle?: number;
  textBoxRadius?: number;
  textBoxPadding?: number;
  textBoxPaddingTop?: number;
  textBoxPaddingLeft?: number;
  textBoxPaddingRight?: number;
  applyFontEffects?: boolean;
  reelBackgroundUrl?: string;
  reelTextCenterX?: number;
  reelTextBottom?: number;
  autoFontMax?: number;
  colonBreak?: boolean;
  textZoneWidth?: number;
  bandGrow?: boolean;
}

export const templatesApi = {
  // Получить все шаблоны
  async getAll(signal?: AbortSignal): Promise<TemplateDTO[]> {
    const response = await api.get<{ templates: TemplateDTO[] }>('/templates', { signal });
    return response.templates;
  },

  // Получить шаблон по ID
  async getById(id: string): Promise<TemplateDTO> {
    const response = await api.get<{ template: TemplateDTO }>(`/templates/${id}`);
    return response.template;
  },

  // Получить шаблон по accountId (+ опционально type). Без type — вернёт первый найденный.
  async getByAccountId(accountId: string, type?: 'reel' | 'post'): Promise<TemplateDTO | null> {
    const qs = type ? `?type=${type}` : '';
    const response = await api.get<{ template: TemplateDTO | null; templates?: TemplateDTO[] }>(
      `/templates/account/${accountId}${qs}`,
    );
    if (type) return response.template || null;
    // Back-compat: старая edge-функция отдаёт { template }, новая — { templates }.
    return response.templates?.[0] || response.template || null;
  },

  // Получить все шаблоны аккаунта (оба типа).
  async getAllByAccountId(accountId: string): Promise<TemplateDTO[]> {
    const response = await api.get<{ template?: TemplateDTO | null; templates?: TemplateDTO[] }>(
      `/templates/account/${accountId}`,
    );
    // Back-compat: старая edge-функция отдаёт { template }, новая — { templates }.
    if (response.templates) return response.templates;
    return response.template ? [response.template] : [];
  },

  // Создать шаблон
  async create(data: CreateTemplateDTO): Promise<TemplateDTO> {
    const response = await api.post<{ template: TemplateDTO }>('/templates', data);
    return response.template;
  },

  // Обновить шаблон
  async update(id: string, data: UpdateTemplateDTO): Promise<TemplateDTO> {
    const response = await api.put<{ template: TemplateDTO }>(`/templates/${id}`, data);
    return response.template;
  },

  // Удалить шаблон
  async delete(id: string): Promise<void> {
    await api.delete<void>(`/templates/${id}`);
  },
};